// src/store/slices/constantsSlice.ts
/**
 * Mathematical constants with localStorage persistence
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ConstantsState {
  enabled: boolean;
  precision: number; // 2-6 decimal places
  visibleConstants: string[]; // Array of constant IDs to show
  showLabels: boolean; // Whether to show the text labels
  labelPosition: 'above' | 'below' | 'left' | 'right'; // Label positioning relative to marker
}

const CONSTANTS_STORAGE_KEY = 'nl:constants';

// Default constants state
const defaultConstantsState: ConstantsState = {
  enabled: false,
  precision: 4, // Default to 4 decimal places
  visibleConstants: [], // No constants visible by default
  showLabels: true,
  labelPosition: 'above'
};

/**
 * Load constants state from localStorage
 */
function loadConstantsFromStorage(): ConstantsState {
  try {
    const stored = localStorage.getItem(CONSTANTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new constant additions
      const constants = { ...defaultConstantsState, ...parsed };
      console.log('📐 Loaded constants state:', constants);
      return constants;
    }
  } catch (error) {
    console.warn('Failed to load constants from storage:', error);
  }
  console.log('📐 Using default constants state:', defaultConstantsState);
  return defaultConstantsState;
}

/**
 * Save constants state to localStorage
 */
function saveConstantsToStorage(constants: ConstantsState): void {
  try {
    localStorage.setItem(CONSTANTS_STORAGE_KEY, JSON.stringify(constants));
  } catch (error) {
    console.warn('Failed to save constants to storage:', error);
  }
}

const initialState: ConstantsState = loadConstantsFromStorage();

const constantsSlice = createSlice({
  name: 'constants',
  initialState,
  reducers: {
    toggleConstants: (state) => {
      state.enabled = !state.enabled;
      saveConstantsToStorage(state);
    },
    
    setConstantsEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;
      saveConstantsToStorage(state);
    },
    
    setPrecision: (state, action: PayloadAction<number>) => {
      // Clamp precision between 2 and 6 decimal places
      state.precision = Math.max(2, Math.min(6, action.payload));
      saveConstantsToStorage(state);
    },
    
    setVisibleConstants: (state, action: PayloadAction<string[]>) => {
      state.visibleConstants = action.payload;
      saveConstantsToStorage(state);
    },
    
    toggleConstantVisibility: (state, action: PayloadAction<string>) => {
      const constantId = action.payload;
      const index = state.visibleConstants.indexOf(constantId);
      
      if (index >= 0) {
        // Remove if present
        state.visibleConstants = state.visibleConstants.filter(id => id !== constantId);
      } else {
        // Add if not present
        state.visibleConstants.push(constantId);
      }
      saveConstantsToStorage(state);
    },
    
    setShowLabels: (state, action: PayloadAction<boolean>) => {
      state.showLabels = action.payload;
      saveConstantsToStorage(state);
    },
    
    setLabelPosition: (state, action: PayloadAction<'above' | 'below' | 'left' | 'right'>) => {
      state.labelPosition = action.payload;
      saveConstantsToStorage(state);
    },
    
    resetConstants: (state) => {
      Object.assign(state, defaultConstantsState);
      saveConstantsToStorage(state);
    },

    /**
     * Load constants state from shared scene
     */
    loadConstantsState: (state, action: PayloadAction<ConstantsState>) => {
      Object.assign(state, action.payload);
      saveConstantsToStorage(state);
    }
  }
});

export const {
  toggleConstants,
  setConstantsEnabled,
  setPrecision,
  setVisibleConstants,
  toggleConstantVisibility,
  setShowLabels,
  setLabelPosition,
  resetConstants,
  loadConstantsState,
} = constantsSlice.actions;

export default constantsSlice.reducer;